import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {DynamicFormService} from '../../../dynamic-form/dynamic-form.service';
import {BaseFieldComponent} from '../../base/base-field/base-field.component';
import {EventDispatcherService} from '../../../events/event-dispatcher.service';
import {TextAreaFieldConfig} from '../text-area-field.config';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {debounceTime, Subject} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TextInputAutocompleteController} from '../../../autocomplete/text-input-autocomplete.controller';
import {TextAutocompletePanelComponent} from '../../../autocomplete/text-autocomplete-panel/text-autocomplete-panel.component';
import {TextAutocompleteConfig} from '../../../autocomplete/text-autocomplete.types';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
    selector: 'mydocma-form-textarea-field',
    templateUrl: './text-area-field.component.html',
    styleUrls: ['./text-area-field.component.scss'],
    providers: [AsyncPipe]
})
export class TextAreaFieldComponent extends BaseFieldComponent<TextAreaFieldConfig> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('autosize', {static: false}) autosize: CdkTextareaAutosize;
    @ViewChild('textInput', {static: false}) textInput: ElementRef<HTMLTextAreaElement>;
    @ViewChild(TextAutocompletePanelComponent, {static: false}) autocompletePanel: TextAutocompletePanelComponent;

    public readonly staticCssClass = 'mydocma-textarea-field';
    public maxLength = 100000000;
    private resize$ = new Subject();

    public autocomplete: TextInputAutocompleteController;

    constructor(
        public readonly dynamicFormService: DynamicFormService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        public readonly eventDispatcher: EventDispatcherService,
        element: ElementRef,
        renderer: Renderer2,
        private readonly translateService: TranslateService
    ) {
        super(dynamicFormService, changeDetectorRef, eventDispatcher, element, renderer);
    }

    ngOnInit() {
        super.ngOnInit();
        this.setMaxLength();

        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) return;

        this.autocomplete = new TextInputAutocompleteController(
            this.control,
            () => this.textInput?.nativeElement ?? null,
            () => this.autocompletePanel ?? null,
            '.mydocma-textarea-field',
            this.changeDetectorRef,
            this.translateService
        );
        this.autocomplete.setup(config);
        
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setMaxLength();
        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) return;

        this.autocomplete.setup(config);
    }

    ngAfterViewInit(): void {
        this.resize$
            .pipe(debounceTime(100), untilDestroyed(this))
            .subscribe((value) => {
                if (this.autosize && value) {
                    this.autosize.resizeToFitContent(true);
                }
            });
    }

    public onResize($event) {
        this.resize$.next($event);
    }

    private setMaxLength() {
        const maxLength = (this.fieldWrapper?.config as TextAreaFieldConfig).maxLength;
        if (!Number.isInteger(maxLength)) return;
        this.maxLength = maxLength;
    }

    public onTextInput(event: Event) {
        const config: TextAutocompleteConfig | undefined = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) return;
        const target = event.target as HTMLTextAreaElement;
        const value = target.value || '';
        const caretIndex = target.selectionStart != null ? target.selectionStart : value.length;
        this.autocomplete.onInput(value, caretIndex);
    }

    public onTextKeydown(event: KeyboardEvent) {
        const config: TextAutocompleteConfig | undefined = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) return;
        this.autocomplete.onKeydown(event);
    }

    public onBlur(event: FocusEvent) {
        const config: TextAutocompleteConfig | undefined = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || this.fieldWrapper?.config?.readonly) {
            this.eventDispatcher.notifyControlBlur(this.fieldWrapper.config);
            return;
        };
        
        const next = event.relatedTarget as HTMLElement | null;
        const panelEl = this.autocompletePanel?.panelEl?.nativeElement ?? null;

        // If focus moves into the autocomplete panel, do not treat it as a real blur.
        if (next && panelEl && panelEl.contains(next)) {
            return;
        }

        this.eventDispatcher.notifyControlBlur(this.fieldWrapper.config);
        this.autocomplete.closePanel();
    }

    public onFieldClick(event?: MouseEvent) {
        if (this.fieldWrapper.config?.readonly && this.fieldWrapper.config?.showClearButtonIfReadonly) {
            super.onControlClick();
        }
        if (!this.fieldWrapper.config.readonly) {
            super.onControlClick();
        }

        const config: TextAutocompleteConfig = this.fieldWrapper?.config?.autocompleteConfig;
        if (!config || !event || this.fieldWrapper?.config?.readonly) return;

        const target = event.target as HTMLTextAreaElement;
        if (!target || typeof target.selectionStart !== 'number') return;
        const caretIndex = target.selectionStart;
        this.autocomplete.onFieldClick(target.value, caretIndex);
    }

    public onAutocompleteOptionSelected(item: { value: string; display?: string }) {
        this.autocomplete.onOptionSelected(item);
    }

    public onAutocompleteOpened() {
        this.autocomplete.onPanelOpened();
    }

    public onAutocompleteClosed() {
        this.autocomplete.onPanelClosed();
    }

    ngOnDestroy(): void {
        this.autocomplete?.destroy();
    }
    
}
